import React, { useRef, useEffect, useState } from "react";
import "../Styles/Travelers_fav_hotel.css";
import api from "../Api";
import { useNavigate } from "react-router-dom";
import noimg from "../Images/noimg.jpg";
import Skeleton from "@mui/material/Skeleton";

const SkeletonCard = () => {
  return (
    <div className="hcar-card">
      <div className="hcar-image">
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </div>
      <div className="hcar-info">
        <Skeleton variant="text" width="60%" height={16} />
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton variant="text" width="50%" height={16} />
        <Skeleton variant="rectangular" width={60} height={22} />
      </div>
    </div>
  );
};

const PropertyCarousel = () => {
  const [isLoading, setIsLoading] = useState(true); // Initial load
  const [isFetchingMore, setIsFetchingMore] = useState(false); // Loading more items
  const [hotelData, setHotelData] = useState([]);
  
  // Pagination States
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 5; // Number of hotels to fetch per request

  const navigate = useNavigate();
  const scrollRef = useRef(null);
  
  // Synchronous lock to prevent duplicate API calls
  const isFetchingRef = useRef(false); 

  // ✅ Unified Fetch Function (Handles Initial Load & Pagination)
  const fetchHotelsData = async (currentPage, isInitial = false) => {
    // 1. Block the call if a fetch is already in progress
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (isInitial) setIsLoading(true);
    else setIsFetchingMore(true);

    try {
      // NOTE: Ensure your backend accepts page and limit query parameters
      const res = await api.get(`/hotels?page=${currentPage}&limit=${LIMIT}`);
      
      if (res.data.status === true) {
        const newData = res.data.data || [];

        // If we received fewer items than the limit, we've reached the end
        if (newData.length < LIMIT) {
          setHasMore(false);
        }

        if (isInitial) {
          setHotelData(newData);
        } else {
          // Append new data and prevent duplicates
          setHotelData((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewData = newData.filter((item) => !existingIds.has(item.id));
            return [...prev, ...uniqueNewData];
          });
        }
      }
    } catch (err) {
      console.error("Error fetching hotels:", err);
    } finally {
      isFetchingRef.current = false; // Unlock
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  // ✅ Initial Fetch
  useEffect(() => {
    fetchHotelsData(1, true);
  }, []);

  // ✅ Fetch when page changes (triggered by scroll)
  useEffect(() => {
    if (page > 1) {
      fetchHotelsData(page, false);
    }
  }, [page]);

  // ✅ Button Navigation Handlers (Renamed from handleScroll to avoid conflict)
  const scrollSlider = (direction) => {
    const container = scrollRef.current;
    if (!container) return;
    const amount = container.offsetWidth / 4;
    container.scrollBy({
      left: direction === "right" ? amount : -amount,
      behavior: "smooth",
    });
  };

  // ✅ Scroll Event Handler for Infinite Scroll
  const handleScroll = () => {
    if (!scrollRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    
    // Check if scrolled within 150px of the right edge
    if (scrollLeft + clientWidth >= scrollWidth - 150) {
      // Check refs and states synchronously
      if (!isFetchingRef.current && hasMore && !isLoading && !isFetchingMore) {
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  // URL slug generation
  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleClick = (resort) => {
    const slug = createSlug(resort?.slug || resort?.name || "resort");
    const id = resort?.id;
    const random = Math.floor(100 + Math.random() * 900);
    const encodedId = id + random;
    const urlPart = `${slug}-${encodedId}-${random}`;
    navigate(`/hotel/${urlPart}`);
  };

  return (
    <section className="hcar-wrapper">
      {hotelData?.length === 0 && !isLoading && (
        <p className="hcar-empty">No favorite hotels found.</p>
      )}

      {hotelData?.length > 0 && (
        <>
          <div className="hcar-header">
            <div className="hcar-title-block">
              <h3 className="hcar-headline">
                Stay at our top unique Hotels
              </h3>
              <p className="hcar-subtext">
                From castles and villas to boats and igloos, we've got it all
              </p>
            </div>

            <div className="hcar-controls">
              {hotelData?.length > 4 && (
                <>
                  <button
                    onClick={() => scrollSlider("left")}
                    aria-label="Previous"
                  >
                    ◀
                  </button>
                  <span
                    className="hcar-show-more"
                    onClick={() => navigate("/hotels")}
                  >
                    +Show More
                  </span>
                  <button
                    onClick={() => scrollSlider("right")}
                    aria-label="Next"
                  >
                    ▶
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Added onScroll event here */}
          <div 
            className="hcar-track" 
            ref={scrollRef}
            onScroll={handleScroll}
          >
            {isLoading
              ? Array.from(new Array(6)).map((_, index) => (
                  <SkeletonCard key={`skeleton-${index}`} />
                ))
              : hotelData.map((item, index) => (
                  <div
                    className="hcar-card"
                    key={`${item.id}-${index}`}
                    onClick={() => handleClick(item)}
                  >
                    <div className="hcar-image">
                      <img
                        src={item.featured_image || noimg}
                        alt={item.name}
                        loading="lazy"
                      />
                    </div>

                    <div className="hcar-info">
                      <div className="hcar-top-row">
                        <span className="hcar-tag">
                          {item.type || "Hotel"}
                        </span>
                        <span className="hcar-price">
                          ₹{item.rooms?.[0]?.price_per_night || 0}/night
                        </span>
                      </div>

                      <h4 className="hcar-name">{item.name}</h4>
                      <p className="hcar-location">{item.location}</p>

                      <div className="hcar-rating-box">
                        <span className="hcar-rating-number">
                          {item.average_rating || 0}
                        </span>
                        <span className="hcar-review-text">
                          {item.reviews?.length || 0} reviews
                        </span>
                      </div>
                    </div>
                  </div>
                ))}

            {/* Loading Skeleton at the end of the slider */}
            {isFetchingMore && (
              <div style={{ minWidth: "250px", height: "100%" }}>
                <SkeletonCard />
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PropertyCarousel;